import React from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, ExternalLink, RefreshCw, Mail } from 'lucide-react';
import type { EmailDeliveryStatus } from '@/types/WeeklyReport';

interface EmailDeliveryStatusCardProps {
  status: EmailDeliveryStatus;
  onRetry?: () => void;
  onDismiss?: () => void;
  onReauthenticate?: () => void;
}

export const EmailDeliveryStatusCard: React.FC<EmailDeliveryStatusCardProps> = ({
  status,
  onRetry,
  onDismiss,
  onReauthenticate
}) => {
  const isAuthError = status.error?.toLowerCase().includes('401') ||
    status.error?.toLowerCase().includes('authentication') ||
    status.error?.toLowerCase().includes('credential') ||
    status.error?.toLowerCase().includes('expired') ||
    status.error?.toLowerCase().includes('unauthenticated');

  const openInGmail = () => {
    if (status.messageId) {
      window.open(`https://mail.google.com/mail/u/0/#sent/${status.messageId}`, '_blank');
    }
  };

  if (status.status === 'sent') {
    return (
      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg mt-4">
        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
        <span className="text-sm text-green-800 flex-1">Email sent</span>
        {status.messageId && (
          <Button variant="ghost" size="sm" onClick={openInGmail} className="text-green-700 hover:text-green-900">
            <ExternalLink className="h-4 w-4 mr-1" />
            View
          </Button>
        )}
        {onDismiss && (
          <Button variant="ghost" size="sm" onClick={onDismiss} className="text-green-700 hover:text-green-900">
            ✕
          </Button>
        )}
      </div>
    );
  }

  if (status.status === 'failed') {
    return (
      <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg mt-4">
        <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
        <span className="text-sm text-red-800 flex-1">
          {status.error || 'Failed to send email'}
        </span>
        {isAuthError ? (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onReauthenticate || (() => window.location.href = '/login')}
            className="text-red-700 hover:text-red-900"
          >
            <Mail className="h-4 w-4 mr-1" />
            Sign in
          </Button>
        ) : onRetry && (
          <Button variant="ghost" size="sm" onClick={onRetry} className="text-red-700 hover:text-red-900">
            <RefreshCw className="h-4 w-4 mr-1" />
            Retry
          </Button>
        )}
        {onDismiss && (
          <Button variant="ghost" size="sm" onClick={onDismiss} className="text-red-700 hover:text-red-900">
            ✕
          </Button>
        )}
      </div>
    );
  }

  // Pending or other status - don't show anything
  return null;
};

export default EmailDeliveryStatusCard;
